/**
 * Exact AED arithmetic for what a form shows before the API decides (SDD §9.2): amounts are
 * decimal strings, summed as whole fils in a bigint, never as JavaScript numbers. The API remains
 * the authority; these figures only tell the user what they are about to send.
 */

const AMOUNT = /^\d{1,17}(\.\d{1,2})?$/;

/** Whether the text is an amount the API accepts: at most 17 digits and 2 decimals. */
export function isAmount(text: string): boolean {
  return AMOUNT.test(text.trim());
}

/** Fils in an amount string; an unparseable or empty amount is zero. */
export function toFils(text: string): bigint {
  const value = text.trim();
  if (!AMOUNT.test(value) && !/^-\d{1,17}(\.\d{1,2})?$/.test(value)) return 0n;
  const negative = value.startsWith('-');
  const [whole = '0', fraction = ''] = value.replace('-', '').split('.');
  const fils = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
  return negative ? -fils : fils;
}

/** An amount string at two decimals, e.g. `1234.50` or `-0.05`. */
export function fromFils(fils: bigint): string {
  const negative = fils < 0n;
  const abs = negative ? -fils : fils;
  const whole = abs / 100n;
  const fraction = (abs % 100n).toString().padStart(2, '0');
  return `${negative ? '-' : ''}${whole.toString()}.${fraction}`;
}

/** The sum of amount strings, as an amount string. */
export function sum(amounts: readonly string[]): string {
  return fromFils(amounts.reduce((total, amount) => total + toFils(amount), 0n));
}
